import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  Menu as MenuIcon,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
} from 'lucide-react';
import { createContext, Fragment, type ReactNode, use, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, IconButton, TextLink } from '@/components/ui/buttons';
import { Avatar, LogoMark, LogoWordmark, Text } from '@/components/ui/display';
import { ToastHost } from '@/components/ui/feedback';
import { ICON_SIZE } from '@/components/ui/iconSize';
import {
  NavDrawer,
  NavGroupToggle,
  NavInset,
  NavItem as NavLinkItem,
  NavRailMark,
} from '@/components/ui/navigation';
import { Menu, type MenuItem } from '@/components/ui/overlays';
import { AppBar } from '@/components/ui/surfaces';
import { useBreakpoint } from '@/hooks/useMeasure';
import type { Theme } from '@/hooks/useTheme';
import { cn } from '@/lib/cn';

interface SlotState {
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
}

const SlotContext = createContext<SlotState | null>(null);

/** Gives one PageActionsSlot and every PageActions below it the same slot. */
export function PageActionsArea({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return <SlotContext value={{ slot, setSlot }}>{children}</SlotContext>;
}

/** Shows the place where the open page puts its actions. */
export function PageActionsSlot({ className }: { className?: string }) {
  return <div ref={use(SlotContext)?.setSlot} className={className} />;
}

/** Puts its children into the page actions slot of the shell. */
export function PageActions({ children }: { children: ReactNode }) {
  const slot = use(SlotContext)?.slot;
  return slot ? createPortal(children, slot) : null;
}

export interface PageHeading {
  title: string;
  subtitle?: string;
  crumb?: { label: string; to: string };
}

export interface AppShellProps {
  sidebar: ReactNode;
  topBar: ReactNode;
  collapsed: boolean;
  heading: PageHeading;
  children: ReactNode;
}

/** Shows the app frame: the nav, the top bar, the page heading with its actions, and the page. */
export function AppShell({ sidebar, topBar, collapsed, heading, children }: AppShellProps) {
  return (
    <div className="min-h-screen">
      {sidebar}

      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding]',
          collapsed ? 'lg:pl-16' : 'lg:pl-56',
        )}
      >
        {topBar}

        <PageActionsArea>
          <main className="mx-auto flex w-full max-w-page flex-col gap-4 p-4 sm:gap-6 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                {heading.crumb && (
                  <TextLink to={heading.crumb.to} small className="inline-flex items-center gap-1">
                    <ArrowLeft size={ICON_SIZE.sm} aria-hidden />
                    {heading.crumb.label}
                  </TextLink>
                )}
                <Text as="h1" variant="pageTitle" tone="heading" truncate className="mt-0.5 block">
                  {heading.title}
                </Text>
                {heading.subtitle && (
                  <Text as="p" tone="muted" className="mt-1">
                    {heading.subtitle}
                  </Text>
                )}
              </div>

              <PageActionsSlot className="flex shrink-0 flex-wrap items-center gap-2.5 empty:hidden" />
            </div>

            {children}
          </main>
        </PageActionsArea>
      </div>

      <ToastHost />
    </div>
  );
}

/** Shows a column at the reading width that form pages share. */
export function PageContent({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex w-full min-w-0 max-w-3xl flex-col gap-4', className)}>{children}</div>;
}

export interface NavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
  open: boolean;
  onToggle: () => void;
  icon?: ReactNode;
}

export type ContextEntry = NavItem | { label: string; icon?: ReactNode; isLabel: true };

export interface SidebarContext {
  /** The nav group of the context. If absent, the context goes after all the groups. */
  groupKey?: string;
  kindLabel: string;
  name: string;
  backTo: string;
  backLabel: string;
  entries: ContextEntry[];
}

export interface SidebarProps {
  groups: NavGroup[];
  context?: SidebarContext;
  collapsed: boolean;
  onToggleCollapse: () => void;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  ariaLabel: string;
  labels: {
    collapse: string;
    expand: string;
  };
}

/** Shows the app nav: fixed on a wide screen, and sliding in on a phone. */
export function Sidebar({
  groups,
  context,
  collapsed,
  onToggleCollapse,
  drawerOpen,
  onCloseDrawer,
  ariaLabel,
  labels,
}: SidebarProps) {
  const isWide = useBreakpoint('lg');
  const collapseLabel = collapsed ? labels.expand : labels.collapse;

  const contextBlock = context && (
    <NavInset compact={collapsed}>
      <NavLinkItem
        to={context.backTo}
        end
        rail={collapsed}
        onSurface
        title={context.backLabel}
        aria-label={context.backLabel}
        className="gap-2 px-2 pb-2 pt-1"
      >
        <Text tone="primary" className="flex shrink-0">
          <ChevronLeft size={collapsed ? ICON_SIZE.md : ICON_SIZE.sm} />
        </Text>
        {!collapsed && (
          <span className="flex min-w-0 flex-col">
            <Text variant="tag" tone="muted">
              {context.kindLabel}
            </Text>
            <Text variant="label" tone="heading" truncate>
              {context.name}
            </Text>
          </span>
        )}
      </NavLinkItem>

      {context.entries.map((entry) =>
        'isLabel' in entry ? (
          collapsed ? (
            <NavRailMark key={entry.label} label={entry.label} icon={entry.icon} />
          ) : (
            <Text key={entry.label} variant="tag" tone="muted" className="whitespace-nowrap px-2 pb-0.5 pt-2.5">
              {entry.label}
            </Text>
          )
        ) : (
          <SidebarItem key={entry.to} item={entry} collapsed={collapsed} onSurface />
        ),
      )}
    </NavInset>
  );

  return (
    <NavDrawer
      permanent={isWide}
      open={drawerOpen}
      onClose={onCloseDrawer}
      collapsed={collapsed}
      ariaLabel={ariaLabel}
      header={
        <>
          {collapsed ? <LogoMark /> : <LogoWordmark className="min-w-0 flex-1 truncate" />}
          <IconButton
            aria-label={collapseLabel}
            title={collapseLabel}
            size="sm"
            onClick={onToggleCollapse}
            className="hidden lg:inline-flex"
          >
            {collapsed ? <PanelLeftOpen size={ICON_SIZE.sm} /> : <PanelLeftClose size={ICON_SIZE.sm} />}
          </IconButton>
        </>
      }
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          {collapsed ? (
            <NavRailMark label={group.label} icon={group.icon} />
          ) : (
            <NavGroupToggle open={group.open} onToggle={group.onToggle}>
              {group.label}
            </NavGroupToggle>
          )}

          {(group.open || collapsed) &&
            group.items.map((item) => <SidebarItem key={item.to} item={item} collapsed={collapsed} />)}
          {context?.groupKey === group.key && contextBlock}
        </Fragment>
      ))}

      {context && !groups.some((group) => group.key === context.groupKey) && contextBlock}
    </NavDrawer>
  );
}

/** Shows one nav link with its icon, and its label when the nav is not a rail. */
function SidebarItem({ item, collapsed, onSurface = false }: { item: NavItem; collapsed: boolean; onSurface?: boolean }) {
  return (
    <NavLinkItem to={item.to} end={item.end} rail={collapsed} onSurface={onSurface} title={collapsed ? item.label : undefined}>
      <span aria-hidden className="inline-flex w-nav-icon shrink-0 justify-center">
        {item.icon}
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
    </NavLinkItem>
  );
}

export interface TopBarProps {
  onOpenDrawer: () => void;
  drawerOpen: boolean;

  theme: Theme;
  onThemeChange: (theme: Theme) => void;

  language: string;
  languages: readonly { code: string; name: string }[];
  onLanguageChange: (code: string) => void;

  user: { name: string; email: string; roleLabel: string };
  isAdmin: boolean;
  onOpenProfile: () => void;
  onOpenOrganization: () => void;
  onSignOut: () => void;

  labels: {
    openMenu: string;
    language: string;
    theme: string;
    themeLight: string;
    themeDark: string;
    themeSystem: string;
    profileMenu: string;
    profile: string;
    organization: string;
    signOut: string;
  };
}

const THEME_ICON: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

/** Shows the top bar: the drawer button, and the language, theme and profile menus. */
export function TopBar({
  onOpenDrawer,
  drawerOpen,
  theme,
  onThemeChange,
  language,
  languages,
  onLanguageChange,
  user,
  isAdmin,
  onOpenProfile,
  onOpenOrganization,
  onSignOut,
  labels,
}: TopBarProps) {
  const ThemeIcon = THEME_ICON[theme];

  const languageItems: MenuItem[] = languages.map((option) => ({
    key: option.code,
    label: option.name,
    icon: option.code === language ? <Check size={ICON_SIZE.sm} /> : undefined,
    onSelect: () => onLanguageChange(option.code),
  }));

  const themeItems: MenuItem[] = (['light', 'dark', 'system'] as const).map((option) => ({
    key: option,
    label: { light: labels.themeLight, dark: labels.themeDark, system: labels.themeSystem }[option],
    icon: theme === option ? <Check size={ICON_SIZE.sm} /> : <span className="w-3.5" aria-hidden />,
    onSelect: () => onThemeChange(option),
  }));

  const profileItems: MenuItem[] = [
    { key: 'profile', label: labels.profile, onSelect: onOpenProfile },
    ...(isAdmin
      ? [{ key: 'org', label: labels.organization, onSelect: onOpenOrganization }]
      : []),
    { key: 'signout', label: labels.signOut, destructive: true, onSelect: onSignOut },
  ];

  return (
    <AppBar>
      <IconButton aria-label={labels.openMenu} aria-expanded={drawerOpen} onClick={onOpenDrawer} className="lg:hidden">
        <MenuIcon size={ICON_SIZE.lg} />
      </IconButton>

      <div className="flex-1" />

      <Menu
        ariaLabel={labels.language}
        trigger={
          <Button variant="ghost" aria-label={labels.language} iconRight={<ChevronDown size={ICON_SIZE.xs} aria-hidden />}>
            {language.toUpperCase()}
          </Button>
        }
        items={languageItems}
      />

      <Menu
        ariaLabel={labels.theme}
        trigger={
          <IconButton aria-label={labels.theme}>
            <ThemeIcon size={ICON_SIZE.md} />
          </IconButton>
        }
        items={themeItems}
      />

      <Menu
        ariaLabel={labels.profileMenu}
        trigger={
          <IconButton aria-label={labels.profileMenu} className="p-0">
            <Avatar name={user.name} size="lg" filled />
          </IconButton>
        }
        items={profileItems}
        header={
          <>
            <Text variant="title" tone="heading">
              {user.name}
            </Text>
            <Text variant="small" tone="muted" truncate>
              {user.email} · {user.roleLabel}
            </Text>
          </>
        }
      />
    </AppBar>
  );
}
